import { AnalyticsContext } from "./AnalyticsContext";
import { ReactNode, useCallback, useMemo, useState, useRef, useEffect } from "react";
import {
    AnalyticsEvent,
    AnalyticsIdentity,
    AnalyticsMethod,
    AnalyticsPageView,
    AnalyticsUserIdentity,
    AnalyticsWorkflowIdentity,
    AnalyticsWorkspaceIdentity
} from "@rapid/analytics/types";
import { useAuth } from "./AuthContext";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import eventTracker from "@rapid/analytics/EventTracker";
import { isEqual } from "lodash";

import * as Sentry from "@sentry/react";

export function AnalyticsProvider({ children }: { children: ReactNode }) {
    // DEFINE ANALYTICS USER IDENTITY START //
    const { user, initialLoadComplete } = useAuth();
    const { currentWorkspace, currentWorkspaceUsage } = useCurrentWorkspace();
    const { currentWorkflow: workflow } = useCurrentWorkflow();

    const userMeta = useMemo(() => {
        if (!user) return;

        return {
            id: user.uid,
            email: user.email,
            name: user.displayName,
            displayName: user.displayName,
            photo: user.photoURL,
            method: user.providerData[0]?.providerId
        } as AnalyticsUserIdentity;
    }, [user]);

    // Extract workspace plan to avoid complex expression in dependency array
    const workspacePlan = currentWorkspaceUsage?.plan;

    const workspaceMeta = useMemo(() => {
        if (!currentWorkspace) return;

        return {
            workspaceId: currentWorkspace.id,
            workspaceUrl: currentWorkspace.url,
            workspaceName: currentWorkspace.name,
            workspacePlan
        } as AnalyticsWorkspaceIdentity;
    }, [currentWorkspace, workspacePlan]);

    const workflowMeta = useMemo(() => {
        if (!workflow) return;

        return {
            workflowId: workflow.id,
            workflowUrl: workflow.url,
            workflowName: workflow.name
        } as AnalyticsWorkflowIdentity;
    }, [workflow]);

    // Use ref to store previous identity for deep comparison
    // ensures stable identity updates
    const prevIdentityRef = useRef<AnalyticsIdentity | undefined>(undefined);

    const identity = useMemo(() => {
        if (!userMeta) return undefined;

        const newIdentity = {
            ...userMeta,
            ...(workspaceMeta ? workspaceMeta : {}),
            ...(workflowMeta ? workflowMeta : {})
        } as AnalyticsIdentity;

        // Only update if the identity has actually changed
        if (isEqual(prevIdentityRef.current, newIdentity)) {
            return prevIdentityRef.current;
        }

        prevIdentityRef.current = newIdentity;
        return newIdentity;
    }, [userMeta, workspaceMeta, workflowMeta]);

    // DEFINE ANALYTICS USER IDENTITY END //

    const prevPageRef = useRef<{ name: AnalyticsPageView | undefined; path: string | undefined }>({
        name: undefined,
        path: undefined
    });

    // DEFINE STABLE CURRENT PAGE //
    const [currentPage, setCurrentPage] = useState<{
        name: AnalyticsPageView | undefined;
        path: string | undefined;
    }>({ name: undefined, path: undefined });

    // ANALYTICS EVENT TRIGGERS START //

    const track = useCallback(
        async ({ event, data }: { event: AnalyticsEvent; data?: Record<string, any> }) => {
            return eventTracker.trackEvent({
                method: AnalyticsMethod.TRACK,
                id: event,
                data,
                identity
            });
        },
        [identity]
    );

    // pass identity to enable stable identity updates
    // this is used in Analytics.tsx to ensure the identity is updated
    // when the identity changes
    const identify = useCallback(async () => {
        if (!initialLoadComplete) return;

        if (Sentry && identity) {
            const sentryUser: Sentry.User = {
                id: identity.id,
                email: identity.email,
                username: identity.name
            };
            Sentry.setUser(sentryUser);
        }

        return eventTracker.trackEvent({
            method: AnalyticsMethod.IDENTIFY,
            id: identity?.id,
            identity
        });
    }, [initialLoadComplete, identity]);

    // pass identity to enable stable page view updates
    // this is used in Analytics.tsx to ensure the page view is updated
    // when the current location changes
    const page = useCallback(async () => {
        if (!initialLoadComplete) return;

        if (isEqual(prevPageRef.current, currentPage)) {
            return;
        }

        prevPageRef.current = currentPage;

        const { name, path } = currentPage;
        const data = { path };

        return eventTracker.trackEvent({ method: AnalyticsMethod.PAGE, id: name, data, identity });
    }, [initialLoadComplete, identity, currentPage]);

    useEffect(() => {
        if (identify) {
            // trigger identify when identity changes
            identify();
        }
    }, [identify]);

    useEffect(() => {
        if (page) {
            // trigger page when current page changes
            page();
        }
    }, [page]);

    // ANALYTICS EVENT TRIGGERS END //

    const analyticsContextValue = useMemo(() => {
        return { identity, currentPage, setCurrentPage, track, identify, page };
    }, [setCurrentPage, currentPage, identity, track, identify, page]);

    return (
        <AnalyticsContext.Provider value={analyticsContextValue}>
            {children}
        </AnalyticsContext.Provider>
    );
}
